import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChatMessage } from '../models/chat.models';
import { RagApiClient } from './rag-api.client';
import { ChatContextService } from './chat-context.service';
import { ChatUiStateService } from './chat-ui-state.service';
import { SecurityService } from '../../services/security.service';

/**
 * ChatSessionService — Manages conversation history and message flow.
 *
 * Responsibilities:
 *  • Maintain `conversationId` (received from backend, persisted in DB)
 *  • Keep ordered message list
 *  • Orchestrate send → loading → response → error cycle
 *  • Let the backend derive user identity from the Bearer token
 *  • Handle conversation continuity across sessions
 *
 * Changes for Conversation Persistence:
 *  • conversationId now comes from backend (not generated locally)
 *  • conversationId is null for first message, then reused for continuity
 */
@Injectable({ providedIn: 'root' })
export class ChatSessionService {

  private conversationId: string | null = null;
  private readonly messagesSubject = new BehaviorSubject<ChatMessage[]>([]);

  /** Partner con el que se envió el último mensaje; detecta cambios de portal. */
  private lastPartnerId: string | null = null;

  /** Se incrementa en cada cambio de conversación (nueva/cargada); permite
   *  descartar respuestas tardías incluso cuando conversationId no cambia
   *  de valor (p.ej. null → null al abandonar un chat sin responder aún). */
  private conversationGeneration = 0;

  /** Observable stream of all messages in the current conversation. */
  readonly messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();

  constructor(
    private ragApi: RagApiClient,
    private contextService: ChatContextService,
    private uiState: ChatUiStateService,
    private securityService: SecurityService
  ) {}

  /**
   * Send a user message and wait for the assistant's response.
   * 
   * Now includes conversation persistence:
   * - Sends conversationId to continue existing conversation (null for new)
   * - Receives conversationId from backend and stores it for continuity
   */
  sendMessage(text: string): void {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length < 3) return;

    this.resetIfPartnerChanged();

    // 1. Add user message to the list
    const userMsg = this.createMessage('user', trimmed);
    this.pushMessage(userMsg);

    // 2. Set loading state
    this.uiState.setLoading(true);
    this.uiState.setError(null);

    // 3. Snapshot de la generación de conversación de esta petición. Si el
    // usuario cambia de conversación (loadConversation/newConversation)
    // mientras el RAG responde, conversationGeneration ya no coincidirá
    // (incluso si conversationId vuelve a ser el mismo valor, p.ej. null) y
    // la respuesta tardía se descarta en vez de pisar lo que se ve en pantalla.
    const requestGeneration = this.conversationGeneration;
    const MIN_LOADING_MS = 800;

    forkJoin([
      this.ragApi.query({
        question: trimmed,
        conversationId: this.conversationId
      }),
      timer(MIN_LOADING_MS)
    ]).pipe(
      map(([response]) => response)
    ).subscribe({
      next: (response) => {
        if (this.conversationGeneration !== requestGeneration) {
          return;
        }

        // Store conversationId from backend for next message
        if (response.conversationId) {
          this.conversationId = response.conversationId;
        }

        const assistantMsg = this.createMessage(
          'assistant',
          response.message,
          response.sources,
          response.toolCalls
        );
        this.pushMessage(assistantMsg);
        this.uiState.setLoading(false);
        // Unread badge should only increment when the panel is closed.
        // While the user is actively chatting (panel open), keep it at 0.
        this.uiState.incrementUnread();
      },
      error: (err: Error) => {
        if (this.conversationGeneration !== requestGeneration) {
          return;
        }
        this.uiState.setError(err.message);
        this.uiState.setLoading(false);
      }
    });
  }

  /**
   * Start a fresh conversation (clear conversationId, clear messages).
   * 
   * Note: conversationId is now null until backend assigns one on first message.
   */
  newConversation(): void {
    this.conversationId = null;
    this.conversationGeneration++;
    this.messagesSubject.next([]);
    this.uiState.setError(null);
    this.uiState.setLoading(false);
  }

  /**
   * Get the current conversation ID.
   * 
   * @returns conversationId (UUID from backend) or null if no conversation started
   */
  getConversationId(): string | null {
    return this.conversationId;
  }

  /**
   * Load an existing conversation from backend.
   * 
   * This method is ready to use but NOT currently called from UI.
   * It will be activated when conversation history features are needed.
   * 
   * @param conversationId - UUID of the conversation to load
   * @param messages - Array of messages to display
   * 
   * @example
   * // Load a previous conversation
   * conversationService.getConversation(id).subscribe(conversation => {
   *   const chatMessages = conversation.messages.map(msg => ({
   *     id: msg.id,
   *     role: msg.role,
   *     text: msg.content,
   *     createdAt: msg.createdAt
   *   }));
   *   chatSessionService.loadConversation(conversation.id, chatMessages);
   * });
   */
  loadConversation(conversationId: string, messages: ChatMessage[]): void {
    this.conversationId = conversationId;
    this.conversationGeneration++;
    this.messagesSubject.next(messages);
    this.uiState.setError(null);
    this.uiState.setLoading(false);
  }

  // ─── Private helpers ──────────────────────────────────────────────

  /**
   * El contexto de otro partner no debe mezclarse en la misma conversación:
   * si el partner efectivo cambió (combo superAdmin, cambio de portal),
   * se arranca conversación nueva de forma transparente.
   */
  private resetIfPartnerChanged(): void {
    const partner = this.securityService.getTokenPartner();
    const current = partner === null || partner === undefined ? null : String(partner);
    if (this.lastPartnerId !== null && current !== this.lastPartnerId) {
      this.newConversation();
    }
    this.lastPartnerId = current;
  }

  private pushMessage(msg: ChatMessage): void {
    const current = this.messagesSubject.value;
    this.messagesSubject.next([...current, msg]);
  }

  private createMessage(
    role: ChatMessage['role'],
    text: string,
    sources?: ChatMessage['sources'],
    toolCalls?: ChatMessage['toolCalls']
  ): ChatMessage {
    return {
      id: this.generateUUID(),
      role,
      text,
      createdAt: new Date().toISOString(),
      sources,
      toolCalls
    };
  }

  /**
   * Generate a UUID for message IDs (frontend only).
   * 
   * Note: conversationId is now generated by backend, not here.
   */
  private generateUUID(): string {
    return crypto.randomUUID?.()
      ?? 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
  }
}
